import { ref, onMounted } from 'vue'
import dayjs from 'dayjs'
import {
  getNotificationPage,
  markNotificationRead,
  NotificationReadStatus,
  isNotificationUnread,
  type NotificationItem
} from '@/api/notification'

export const NOTIFICATION_PAGE_SIZE = 5
export const NOTIFICATION_LIST_WIDTH = 300
export const NOTIFICATION_DETAIL_WIDTH = 530

export function formatNotificationTime(timestamp?: number) {
  if (!timestamp) return ''
  return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss')
}

export function useNotification() {
  const popoverVisible = ref(false)
  const hasUnread = ref(false)
  const viewMode = ref<'list' | 'detail'>('list')

  const list = ref<NotificationItem[]>([])
  const listLoading = ref(false)
  const listError = ref(false)
  const pageNum = ref(1)
  const total = ref(0)

  const detail = ref<NotificationItem | null>(null)
  const detailError = ref(false)

  const fetchUnreadStatus = async () => {
    try {
      const res = await getNotificationPage({
        pageNum: 1,
        size: 10,
        readStatus: NotificationReadStatus.Unread
      })
      if (res.success) {
        hasUnread.value = (res.data?.total ?? 0) > 0
      }
    } catch {
      // ignore unread status errors
    }
  }

  const fetchList = async () => {
    listLoading.value = true
    listError.value = false
    try {
      const res = await getNotificationPage({
        pageNum: pageNum.value,
        size: NOTIFICATION_PAGE_SIZE
      })
      if (res.success) {
        list.value = res.data?.records ?? []
        total.value = res.data?.total ?? 0
      }
    } catch {
      listError.value = true
    } finally {
      listLoading.value = false
    }
  }

  const handlePopoverShow = () => {
    viewMode.value = 'list'
    detail.value = null
    detailError.value = false
    pageNum.value = 1
    fetchList()
  }

  const handlePageChange = (page: number) => {
    pageNum.value = page
    fetchList()
  }

  const openDetail = async (id: number) => {
    viewMode.value = 'detail'
    detailError.value = false

    const item = list.value.find((row) => row.id === id)
    if (!item) {
      detail.value = null
      detailError.value = true
      return
    }

    detail.value = item

    if (!isNotificationUnread(item)) {
      return
    }

    try {
      await markNotificationRead({ id })
      item.readStatus = NotificationReadStatus.Read
      detail.value = { ...item }
      await fetchUnreadStatus()
    } catch {
      // detail shown; read state unchanged; global request handles error toast
    }
  }

  const backToList = () => {
    viewMode.value = 'list'
    detail.value = null
    detailError.value = false
  }

  const handleRefreshList = async () => {
    await Promise.all([fetchList(), fetchUnreadStatus()])
  }

  onMounted(() => {
    fetchUnreadStatus()
  })

  return {
    popoverVisible,
    hasUnread,
    viewMode,
    list,
    listLoading,
    listError,
    pageNum,
    total,
    detail,
    detailError,
    handlePopoverShow,
    handlePageChange,
    openDetail,
    backToList,
    handleRefreshList
  }
}
